var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState } from 'react';
import { cx, css as emotionCss } from 'emotion';
import { getChildByType } from 'react-nanny';
import { Icon } from '@citrus/icon';
import { getAutomation, getDebug, getStyleNames } from '@citrus/runtime';
import { useLocalization, useTheme } from '@citrus/user';
import { isNotNilOrEmpty, isNilOrEmpty, iif } from '@pcty/utils';
import { Description, IconContainer, ItemImage, Item as Component, SelectedIconContainer, Label, ImageContainer, TextContainer, ComponentTop, } from './VisualPicker.styled';
import Footer from './VisualPickerFooter';
import localizationMap from '../localization';
import { error as errorLegacy, itemImageDisabled as itemImageDisabledLegacy, imageCircle as imageCircleLegacy, imageSquare as imageSquareLegacy, selectedDisabled as selectedDisabledLegacy, disabled as disabledStylesLegacy, selected as selectedStylesLegacy, itemColumns as itemColumnsLegacy, } from '../styles/legacy';
import { error as errorCitron, itemImageDisabled as itemImageDisabledCitron, imageCircle as imageCircleCitron, imageSquare as imageSquareCitron, selectedDisabled as selectedDisabledCitron, disabled as disabledStylesCitron, selected as selectedStylesCitron, itemColumns as itemColumnsCitron, } from '../styles/citron';
var VERSION = '6.5.0';
/** DISPLAY NAME: &lt;VisualPicker.Item&gt; */
var VisualPickerItem = React.forwardRef(function (_a, ref) {
    var _b, _c;
    var automationId = _a.automationId, className = _a.className, css = _a.css, disabled = _a.disabled, description = _a.description, footer = _a.footer, renderFooterActionArea = _a.renderFooterActionArea, renderIcon = _a.renderIcon, label = _a.label, value = _a.value, _d = _a.imageAlt, imageAlt = _d === void 0 ? '' : _d, imageUrl = _a.imageUrl, imageUrlFallback = _a.imageUrlFallback, _e = _a.imageShape, imageShape = _e === void 0 ? 'circle' : _e, __error = _a.__error, __groupId = _a.__groupId, __index = _a.__index, __maxColumns = _a.__maxColumns, __onChange = _a.__onChange, _f = _a.__selected, __selected = _f === void 0 ? false : _f, _g = _a.__TYPE, TYPE = _g === void 0 ? 'VisualPickerItem' : _g, other = __rest(_a, ["automationId", "className", "css", "disabled", "description", "footer", "renderFooterActionArea", "renderIcon", "label", "value", "imageAlt", "imageUrl", "imageUrlFallback", "imageShape", "__error", "__groupId", "__index", "__maxColumns", "__onChange", "__selected", "__TYPE"]);
    var lang = useLocalization(localizationMap);
    var isCitron = useTheme().citrus2022;
    var disabledStyles = iif(isCitron, disabledStylesCitron, disabledStylesLegacy);
    var selectedStyles = iif(isCitron, selectedStylesCitron, selectedStylesLegacy);
    var error = iif(isCitron, errorCitron, errorLegacy);
    var selectedDisabled = iif(isCitron, selectedDisabledCitron, selectedDisabledLegacy);
    var itemColumns = iif(isCitron, itemColumnsCitron, itemColumnsLegacy);
    var imageCircle = iif(isCitron, imageCircleCitron, imageCircleLegacy);
    var imageSquare = iif(isCitron, imageSquareCitron, imageSquareLegacy);
    var itemImageDisabled = iif(isCitron, itemImageDisabledCitron, itemImageDisabledLegacy);
    var pickerItemEmotionObj = (_b = {},
        _b[emotionCss(disabledStyles)] = disabled,
        _b[emotionCss(selectedStyles)] = __selected,
        _b[emotionCss(error)] = __error && !disabled,
        _b[emotionCss(selectedDisabled)] = disabled && __selected,
        _b[emotionCss(itemColumns(__maxColumns))] = __maxColumns,
        _b[emotionCss(css)] = isNotNilOrEmpty(css),
        _b);
    var classes = cx(pickerItemEmotionObj, className || '');
    var pickerItemStyleNames = {
        error: pickerItemEmotionObj[emotionCss(error)],
        selectedDisabled: pickerItemEmotionObj[emotionCss(selectedDisabled)],
        'itemColumns(maxColumns)': pickerItemEmotionObj[emotionCss(itemColumns(__maxColumns))],
    };
    var imgEmotionObj = (_c = {},
        _c[emotionCss(imageCircle)] = imageShape === 'circle',
        _c[emotionCss(imageSquare)] = imageShape === 'square',
        _c[emotionCss(itemImageDisabled)] = disabled,
        _c);
    var imgClasses = cx(imgEmotionObj);
    var imgStyleNames = {
        imageCircle: imgEmotionObj[emotionCss(imageCircle)],
        imageSquare: imgEmotionObj[emotionCss(imageSquare)],
        itemImageDisabled: imgEmotionObj[emotionCss(itemImageDisabled)],
    };
    var _h = useState(false), isImageFallbackSet = _h[0], setIsImageFallbackSet = _h[1];
    var renderImage = function (_a) {
        var imageAlt = _a.imageAlt, imageUrl = _a.imageUrl, imgStyleNames = _a.imgStyleNames, imgClasses = _a.imgClasses, automationId = _a.automationId, imageUrlFallback = _a.imageUrlFallback;
        var onImageError = function (_a) {
            var currentTarget = _a.currentTarget;
            if (isNotNilOrEmpty(imageUrlFallback) && !isImageFallbackSet) {
                currentTarget.src = imageUrlFallback;
                setIsImageFallbackSet(true);
            }
        };
        if (isNilOrEmpty(imageUrl)) {
            return null;
        }
        return (React.createElement(ImageContainer, __assign({}, getAutomation(automationId, 'image')),
            React.createElement(ItemImage, { alt: imageAlt, className: imgClasses, onError: onImageError, src: imageUrl, styleNames: getStyleNames(imgStyleNames) })));
    };
    var handleChange = function () {
        __onChange({ id: __groupId, index: __index, value: value });
    };
    return (React.createElement(Component, __assign({ "aria-checked": __selected, className: classes, disabled: disabled, onClick: handleChange, ref: ref, styleNames: getStyleNames(pickerItemStyleNames), type: "button" }, other, getAutomation(automationId), getDebug(sessionStorage.getItem('citrus-debug') === 'true', TYPE, VERSION)),
        React.createElement(ComponentTop, null,
            renderImage({ imageAlt: imageAlt, imageUrl: imageUrl, imgStyleNames: imgStyleNames, imgClasses: imgClasses, automationId: automationId, imageUrlFallback: imageUrlFallback }),
            _renderIcon(renderIcon, isNotNilOrEmpty(imageUrl)),
            React.createElement(TextContainer, null,
                React.createElement(Label, null, label),
                React.createElement(Description, null, description)),
            renderSelectedIcon(__selected, lang)),
        _renderFooter({ footerText: footer, disabled: disabled, renderFooterActionArea: renderFooterActionArea })));
});
var _renderFooter = function (_a) {
    var footerText = _a.footerText, disabled = _a.disabled, renderFooterActionArea = _a.renderFooterActionArea;
    if (isNilOrEmpty(footerText) && isNilOrEmpty(renderFooterActionArea)) {
        return null;
    }
    return (React.createElement(Footer, { disabled: disabled, renderActionArea: renderFooterActionArea }, footerText));
};
var _renderIcon = function (renderIcon, image) {
    if (!renderIcon || image)
        return;
    var icon = getChildByType(renderIcon(), ['Icon', 'PctyIcon']);
    if (isNilOrEmpty(icon))
        return;
    return React.createElement(IconContainer, null, React.cloneElement(icon, { size: 'medium' }));
};
var renderSelectedIcon = function (selected, lang) {
    return selected ? (React.createElement(SelectedIconContainer, null,
        React.createElement(Icon, { bold: true, className: "vp-item-icon-2l34kj", name: "checkmark-circle", size: "medium", title: lang.selected }))) : undefined;
};
VisualPickerItem.defaultProps = {
    __TYPE: 'VisualPickerItem',
};
export default VisualPickerItem;
